// ============================================================================
// EVIDENCE MAP DRAFTING — deterministic, local authoring assistant
// (Phase 2C-1.5 behavior; Phase 2C-2b: now consumes evidenceConceptLibrary.js)
//
// Drafts SUGGESTED Pack-local evidence maps from Pack text only (labels,
// options, helpText, descriptions, attachment labels/types). The specialist
// must review/edit/accept before anything is saved or published — nothing here
// is used live.
//
// PRINCIPLE:
//   AI drafts meaning. Specialist owns meaning. Behavior proves meaning later.
//   Approval promotes meaning later.
//
// PHASE 2C-2b CHANGE (behavior-preserving refactor):
//   The inline concept dictionaries that used to live here (LAB_CONCEPTS,
//   FINDING_CONCEPTS, RED_FLAG_CONCEPTS, DOC_TYPE_LABEL_HINTS) are GONE. The
//   single concept source is now src/core/evidenceConceptLibrary.js. This module
//   keeps only field-type/label-generation logic (which needs FIELD/label context)
//   and orchestrates: recognize a concept via the library, then project it into
//   the existing 2C-1 authoring shapes via the library's pure projection helpers.
//   Public API and drafting output are preserved (with two documented, intentional
//   equivalences: CBC aliases are enriched with FBC/full blood count, and a
//   recognized finding in a field label now uses the concept's canonical label).
//
// PURE module (no Firebase, no I/O, no AI API, NO patient data). It inspects
// ONLY Never-PHI Pack text and returns Never-PHI evidence-map suggestions in the
// exact shapes authored in Phase 2C-1. Normalization + validation are owned by
// evidenceMap.js — this module does not re-implement them.
//
// SCOPE: drafting suggestions only. NO matching against patient docs, NO chips,
// NO candidates, NO Gate changes, NO behavior learning, NO concept-library
// persistence, NO auto-linking on Save/Publish.
// ============================================================================

import { reconcileDocumentTypeHint, normalizeStringList } from './evidenceMap.js';
import {
  RECOGNITION_GROUPS,
  findConceptByText,
  inferDocumentTypeFromText,
  conceptToFieldEvidenceMap,
  conceptToOptionEvidenceMap,
  conceptToRedFlagEvidenceMap,
  conceptToAttachmentEvidenceMap,
} from './evidenceConceptLibrary.js';

// ── small pure helpers ───────────────────────────────────────────────────────

// Trim + drop-empty + case-insensitive dedupe (preserve first casing).
const dedupe = (list) => normalizeStringList(list);

/** Slugify a label into a conceptId fallback (e.g. "Latest WBC" → "latest_wbc"). */
const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

// Field-type-aware defaults (drives valueType/evidenceMode/units for FIELD drafts).
// This is FIELD semantics, not concept data, so it stays in the drafter.
const FIELD_TYPE_DEFAULTS = {
  number:      { valueType: 'number',  evidenceMode: 'nearby_value',  units: true },
  severity:    { valueType: 'number',  evidenceMode: 'evidence_only', units: false },
  slider:      { valueType: 'number',  evidenceMode: 'evidence_only', units: false },
  date:        { valueType: 'date',    evidenceMode: 'nearby_value',  units: false },
  select:      { valueType: 'option',  evidenceMode: 'option_alias',  units: false },
  radio:       { valueType: 'option',  evidenceMode: 'option_alias',  units: false },
  multiselect: { valueType: 'option',  evidenceMode: 'option_alias',  units: false },
  checkbox:    { valueType: 'option',  evidenceMode: 'option_alias',  units: false },
  boolean:     { valueType: 'boolean', evidenceMode: 'keyword',       units: false },
  textarea:    { valueType: 'text',    evidenceMode: 'evidence_only', units: false },
  text:        { valueType: 'text',    evidenceMode: 'evidence_only', units: false },
};

// Leading qualifiers stripped to build a generic red-flag "core" phrase.
const RED_FLAG_QUALIFIERS = /^(active|acute|severe|suspected|possible|probable|chronic|ongoing|new|worsening)\s+/i;

// Ordered recognition candidates for FIELD drafting: LAB concepts first, then
// FINDING concepts — identical precedence to the former
// matchConcept(text, LAB_CONCEPTS) || matchConcept(text, FINDING_CONCEPTS).
const FIELD_RECOGNITION_IDS = [...RECOGNITION_GROUPS.LAB, ...RECOGNITION_GROUPS.FINDING];

// ============================================================================
// DRAFTERS — each returns a Never-PHI evidence-map suggestion in the exact
// Phase 2C-1 authoring shape. The UI populates the existing inputs with these;
// the specialist edits/accepts; normalization/validation run on save/publish.
// ============================================================================

/** Draft a field-level evidence map from field text + type. */
export const draftFieldEvidenceMap = (field = {}) => {
  const label = String(field.label || '').trim();
  const helpText = String(field.helpText || '').trim();
  const searchText = `${label} ${helpText}`;
  const td = FIELD_TYPE_DEFAULTS[field.type] || { valueType: '', evidenceMode: 'keyword', units: false };

  const concept = findConceptByText(searchText, FIELD_RECOGNITION_IDS);
  if (concept) {
    return conceptToFieldEvidenceMap(concept, {
      valueType: td.valueType,
      evidenceMode: td.evidenceMode,
      includeUnits: td.units,
    });
  }

  // Fallback (no concept recognized): conservative slug + label-only alias.
  return {
    conceptId: slugify(label),
    canonicalLabel: label,
    aliases: dedupe([label]),
    phrases: [],
    valueType: td.valueType || '',
    units: [],
    evidenceMode: td.evidenceMode || '',
    negativePhrases: [],
    uncertaintyPhrases: [],
  };
};

/** Draft one option's evidence from its label/value (Part C/D). */
export const draftOptionEvidence = (optionLabel = '') => {
  const term = String(optionLabel || '').trim();
  const concept = findConceptByText(term, RECOGNITION_GROUPS.FINDING);
  return conceptToOptionEvidenceMap(concept, term);
};

/**
 * Draft a red-flag evidence map. ALWAYS positive_only + requireExplicitPositive
 * Evidence true; includes negative + uncertainty phrases. Never any auto-select
 * language.
 */
export const draftRedFlagEvidenceMap = (redFlag = {}) => {
  const label = String(redFlag.label || '').trim();
  const description = String(redFlag.description || '').trim();

  const concept = findConceptByText(`${label} ${description}`, RECOGNITION_GROUPS.RED_FLAG);
  const projected = conceptToRedFlagEvidenceMap(concept);
  if (projected) return projected;

  // Fallback (no concept recognized): derive conservative phrases from the label.
  let positivePhrases = [];
  let negativePhrases = [];
  let uncertaintyPhrases = [];
  if (label) {
    const core = label.replace(RED_FLAG_QUALIFIERS, '').trim() || label;
    const lc = label.toLowerCase();
    const cc = core.toLowerCase();
    positivePhrases = dedupe([label, core]);
    negativePhrases = dedupe([`denies ${cc}`, `no ${cc}`, `no ${lc}`]);
    uncertaintyPhrases = dedupe([`rule out ${cc}`, `history of ${cc}`]);
  }

  return {
    positivePhrases,
    negativePhrases,
    uncertaintyPhrases,
    evidenceMode: 'positive_only',
    requireExplicitPositiveEvidence: true,
  };
};

/** Draft an attachment-requirement evidence map (Part D). */
export const draftAttachmentEvidenceMap = (attachment = {}) => {
  const label = String(attachment.label || '').trim();
  const description = String(attachment.description || '').trim();
  const searchText = `${label} ${description}`;

  // documentTypeHints: reconcile the attachmentType (primary) + first label
  // inference. Both resolve to the canonical evidence document-type enum.
  const hints = [];
  const fromType = reconcileDocumentTypeHint(attachment.attachmentType);
  if (fromType) hints.push(fromType);
  const fromLabel = inferDocumentTypeFromText(searchText);
  if (fromLabel) hints.push(fromLabel);

  const concept = findConceptByText(searchText, RECOGNITION_GROUPS.LAB);
  return conceptToAttachmentEvidenceMap(concept, { label, documentTypeHints: hints });
};

/**
 * True when an evidence-map object already holds reviewable values. Used by the
 * UI to decide whether to confirm before replacing with a fresh draft.
 */
export const evidenceMapHasValues = (em) => {
  if (!em || typeof em !== 'object') return false;
  if (String(em.conceptId || '').trim()) return true;
  if (String(em.canonicalLabel || '').trim()) return true;
  if (Array.isArray(em.documentTypeHints) && em.documentTypeHints.length > 0) return true;
  const listKeys = ['aliases', 'phrases', 'units', 'positivePhrases', 'negativePhrases', 'uncertaintyPhrases'];
  return listKeys.some((k) => Array.isArray(em[k]) && em[k].some((s) => typeof s === 'string' && s.trim()));
};
